import { forwardRef, useState } from 'react';
import { ColorRepresentation } from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';

import { Line, LineProps, useCursor } from '@react-three/drei';
import { MeshProps } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';
import { useDrag } from '@use-gesture/react';

export interface RectControlsResizeHandlerProps extends Omit<MeshProps, 'geometry'> {
  color?: ColorRepresentation;
  lineWidth?: number;
  corner?: boolean;
  cursor?: React.CSSProperties['cursor'];
  length?: number;
  onDrag?: (evt: {
    movement: [number, number];
    down: boolean;
    first: boolean;
    initial: [number, number];
    last: boolean;
    event: PointerEvent | MouseEvent | TouchEvent | KeyboardEvent;
    delta: [number, number];
  }) => void;
}

export const RectControlsResizeHandler = forwardRef<
  Line2 | LineSegments2,
  RectControlsResizeHandlerProps
>(
  (
    {
      color = 'white',
      lineWidth,
      children,
      corner = false,
      cursor = 'resize',
      length = 0.25,
      onDrag,
      ...restProps
    },
    ref
  ) => {
    const [hovered, setHover] = useState(false);

    useCursor(hovered, cursor);

    const handleOver = useEventCallback<PickProp<LineProps, 'onPointerOver'>>(() => {
      setHover(true);
    });

    const handleOut = useEventCallback<PickProp<LineProps, 'onPointerOut'>>(() => {
      setHover(false);
    });

    const bind = useDrag(({ movement: [x, y], down, initial, first, delta, last, event }) => {
      onDrag?.({ movement: [x, y], initial, down, first, delta, event, last });
    }) as any;

    return (
      <Line
        ref={ref}
        {...restProps}
        points={
          corner
            ? [
                [-length, 0],
                [0, 0],
                [0, -length],
              ]
            : [
                [-length * 0.5, 0],
                [length * 0.5, 0],
              ]
        }
        onPointerOut={handleOut}
        onPointerOver={handleOver}
        {...bind()}
        color={color}
        depthTest={false}
        lineWidth={lineWidth}
      >
        {children}
      </Line>
    );
  }
);

export default RectControlsResizeHandler;
